import deepmerge from 'deepmerge'
import { IntegrationServiceId } from '../../common/integrations'
import { ROUTER_BASEPATH } from '../../common/constants'
import { wealthboxSettingsDefaultValues, WealthboxSettingsFormValues } from './IntegrationManagement/IntegrationSpecificSettings/Wealthbox/models'
import { redtailSettingsDefaultValues, RedtailSettingsFormValues } from './IntegrationManagement/IntegrationSpecificSettings/Redtail/model'
import {
  blackDiamondSettingsDefaultValues,
  BlackDiamondSettingsFormValues,
  elementsSettingsDefaultValues,
  ElementsSettingsFormValues,
  practifiSettingsDefaultValues,
  PractifiSettingsFormValues,
  salesforceSettingsDefaultValues,
  SalesforceSettingsFormValues,
  xlr8SettingsDefaultValues,
  XLR8SettingsFormValues,
} from './IntegrationManagement/IntegrationSpecificSettings/SalesforceBased/models'
import { DetailedCRMAccountIntegrationMapping } from '../../models/Account'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const merge = (x: { [key: string]: any }, y: { [key: string]: any }) =>
  deepmerge(x, y, {
    arrayMerge: (_, source) => source,
  })

export const getIntegrationEnableUrl = (accountId: string, serviceId: IntegrationServiceId, redirectUri: string, state: string, code?: string) => {
  const enableUrl = `${import.meta.env.REACT_APP_IAM_API_BASE_URL}/accounts/${accountId}/user-integrations/${serviceId}/enable?redirect_uri=${redirectUri}&state=${state}`
  return code ? `${enableUrl}&code=${code}` : enableUrl
}

export const getIntegrationRedirectUri = (serviceId: string) => {
  const mappedServiceId = serviceId === IntegrationServiceId.E_MONEY ? 'eMoney' : serviceId
  const redirectUrl = window.location.origin + ROUTER_BASEPATH + `/integrations/callback/${mappedServiceId}`
  return encodeURIComponent(redirectUrl)
}

export type IntegrationSettingsFormValues =
  | WealthboxSettingsFormValues
  | RedtailSettingsFormValues
  | SalesforceSettingsFormValues
  | PractifiSettingsFormValues
  | XLR8SettingsFormValues
  | ElementsSettingsFormValues
  | BlackDiamondSettingsFormValues

export const getDefaultIntegrationSettingsFormValues = (serviceId: string): IntegrationSettingsFormValues => {
  if (serviceId === 'wealthbox') {
    return wealthboxSettingsDefaultValues
  } else if (serviceId === 'redtail') {
    return redtailSettingsDefaultValues
  } else if (serviceId === 'salesforce') {
    return salesforceSettingsDefaultValues
  } else if (serviceId === 'practifi') {
    return practifiSettingsDefaultValues
  } else if (serviceId === 'elements') {
    return elementsSettingsDefaultValues
  } else if (serviceId === 'blackdiamond') {
    return blackDiamondSettingsDefaultValues
  } else {
    return xlr8SettingsDefaultValues
  }
}

const deepEqual = (obj1: unknown, obj2: unknown, path: string = 'root'): boolean => {
  if (obj1 === obj2) {
    return true
  }

  if ((obj1 === null || obj1 === undefined) && (obj2 === null || obj2 === undefined)) {
    return true
  }

  if (obj1 === null || obj1 === undefined || obj2 === null || obj2 === undefined || typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return false
  }

  const isArray1 = Array.isArray(obj1)
  const isArray2 = Array.isArray(obj2)
  if (isArray1 !== isArray2) {
    return false
  }

  if (isArray1 && isArray2) {
    if (obj1.length !== obj2.length) {
      return false
    }
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i], `${path}[${i}]`)) {
        return false
      }
    }
    return true
  }

  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)
  const allKeys = new Set([...keys1, ...keys2])

  const obj1Record = obj1 as Record<string, unknown>
  const obj2Record = obj2 as Record<string, unknown>

  for (const key of allKeys) {
    const val1 = obj1Record[key]
    const val2 = obj2Record[key]

    if (!deepEqual(val1, val2, `${path}.${key}`)) {
      return false
    }
  }

  return true
}

export const areMappingsEqual = (
  a: DetailedCRMAccountIntegrationMapping | null | undefined,
  b: DetailedCRMAccountIntegrationMapping | null | undefined,
): boolean => {
  if (a === b) {
    return true
  }

  if (!a || !b) {
    return false
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { limit: limitA, ...restA } = a
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { limit: limitB, ...restB } = b

  return deepEqual(restA, restB)
}
